/**
 * Leviathan Web - Error Boundary
 * Quasar Prime LLC
 *
 * WHY THIS EXISTS.
 *
 * React unmounts the entire tree when a render throws and nothing catches it.
 * The result is a white page: no header, no sidebar, no message, and — because
 * the router never navigated — no history entry to go back to. Browser back
 * and forward both land on the same dead page, which is exactly what the
 * operator evaluating this product reported when entering an order.
 *
 * That has now happened twice for two unrelated reasons (a bad import in
 * DashboardGrid took out all ~90 routes; an error OBJECT put into `setError`
 * took out order entry), which makes it a property of the application rather
 * than a bug in a component. A boundary does not stop either mistake. It stops
 * a mistake in one screen from destroying every other screen, and it leaves
 * the operator something to read and somewhere to click.
 */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /**
   * Changing this value clears a caught error and re-renders the children.
   * Route-level boundaries pass the current path, so navigating away from a
   * broken screen recovers without a reload.
   */
  resetKey?: string;
  /** Shown above the message, e.g. "Orders". */
  label?: string;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the stack: the fallback offers it behind a disclosure so a customer
    // can paste something useful into a support message instead of "it went
    // blank".
    this.setState({ componentStack: info.componentStack ?? null });
    console.error('[ErrorBoundary] caught a render error', error, info.componentStack);
  }

  override componentDidUpdate(prevProps: Props): void {
    if (this.state.error !== null && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  private reset = (): void => {
    this.setState({ error: null, componentStack: null });
  };

  override render(): ReactNode {
    const { error, componentStack } = this.state;
    if (error === null) return this.props.children;

    const { label } = this.props;

    return (
      <div className="relative stat-card" role="alert">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-error-red" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-error-red" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-error-red" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-error-red" />

        <div className="bg-panel-dark border border-error-red/50 p-8 clip-path-octagon">
          <h2 className="text-2xl font-display text-error-red uppercase tracking-wide mb-2">
            This screen stopped working
          </h2>
          <p className="text-text-secondary font-mono text-sm mb-1">
            {label ? `Something went wrong in ${label}.` : 'Something went wrong on this page.'}{' '}
            Nothing you were looking at has been saved or deleted by this error.
          </p>
          <p className="text-text-tertiary font-mono text-xs mb-6">
            The rest of the application still works — use the menu, or one of the buttons below.
          </p>

          <div className="bg-background border border-panel-border p-4 mb-6">
            <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-2">
              Error
            </div>
            <div className="text-error-red font-mono text-sm break-words">
              {error.message || String(error)}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-6">
            <button
              type="button"
              onClick={this.reset}
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-2 px-6 uppercase tracking-wider text-sm"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => window.history.back()}
              className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-6 uppercase tracking-wider text-sm"
            >
              Go Back
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-6 uppercase tracking-wider text-sm"
            >
              Reload
            </button>
          </div>

          <details className="text-text-tertiary font-mono text-xs">
            <summary className="cursor-pointer hover:text-amber uppercase tracking-widest">
              Technical details
            </summary>
            <pre className="mt-3 whitespace-pre-wrap break-words max-h-64 overflow-y-auto bg-background border border-panel-border p-3">
              {error.stack || error.message}
              {componentStack ? `\n\nComponent stack:${componentStack}` : ''}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
